import {useMemo, useState} from "react";
import {ContainerEntity} from "@ots-frontend/ots-data";
import {useDebouncedValue} from "@mantine/hooks";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";

export type UseContainerDataTableFilterProps = Omit<UseDataTableFilterProps<ContainerEntity>, 'filterConfig'>

export function useContainerDataTableFilter(props: UseContainerDataTableFilterProps): UseDataTableFilterResult<ContainerEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const [freightTypeQuery, setFreightTypeQuery] = useState<string | undefined>(undefined);
  const [freightDebouncedTypeQuery] = useDebouncedValue(freightTypeQuery, 200);

  const [barcodeQuery, setBarcodeQuery] = useState<string | undefined>(undefined);
  const [barcodeDebouncedQuery] = useDebouncedValue(barcodeQuery, 200);

  const filterConfig: DataTableFilterConfig<ContainerEntity> = useMemo(() => {
    return {
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: (record, query) => !record.name.toLowerCase().includes(query.toLowerCase())
      },
      "freightType": {
        setQuery: setFreightTypeQuery,
        query: freightTypeQuery,
        debouncedQuery: freightDebouncedTypeQuery,
        filter: (record, query) => !record.waterTypeName || !record.waterTypeName.toLowerCase().includes(query.toLowerCase())
      },
      "barcode": {
        setQuery: setBarcodeQuery,
        query: barcodeQuery,
        debouncedQuery: barcodeDebouncedQuery,
        filter: (record, query) => !record.barcode || !record.barcode.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    nameQuery,
    nameDebouncedQuery,
    freightTypeQuery,
    freightDebouncedTypeQuery,
    barcodeQuery,
    barcodeDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
